import classNames from "classnames";
import { FeatureFlag, useFeatureGate } from "features/Statsig";
import NewThreadInput from "features/NewThreadButton/components/NewThreadInput";
import { convertPaperDetailsToPaper, Paper, PaperDetails } from "helpers/api";
import useLabels from "hooks/useLabels";
import { useEffect, useMemo, useRef, useState } from "react";

type AskPaperButtonProps = {
  onClick: () => void;
  paper: PaperDetails;
};

function AskPaperButton({ onClick, paper }: AskPaperButtonProps) {
  const [askPaperLabels] = useLabels("ask-paper");
  const containerRef = useRef<HTMLDivElement>(null);

  const [isInView, setIsInView] = useState(false);

  const isDataSourceSelectorEnabled = useFeatureGate(
    FeatureFlag.DATA_SOURCE_SELECTOR
  ).value;

  const handleScroll = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const bottomOffset = 24; // Adjusted to match "bottom-6" for sticky positioning (20px)
      setIsInView(rect.bottom < window.innerHeight - bottomOffset);
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const sources = useMemo(() => {
    return [convertPaperDetailsToPaper(paper)];
  }, [paper]);

  return (
    <div
      ref={containerRef}
      className={classNames(
        "flex fixed right-0 bottom-6 z-10 justify-around mt-4 left-(--sidebar-width)"
      )}
    >
      {/* With the paper sidebar drawer enabled, redirect "ask paper" questions to a new thread instead. */}
      {isDataSourceSelectorEnabled ? (
        <div className="mx-auto w-full max-w-page">
          <NewThreadInput sources={sources} disallowRemovingSources />
        </div>
      ) : (
        <button
          onClick={() => onClick()}
          className={`flex text-fg-muted hover:text-fg-base items-center gap-2 pl-4 pr-1.5 py-1.5 bg-bg-base shadow-elevation-default border border-border-default rounded-2xl transition-all outline-0 ${
            isInView ? "xs:w-full" : "xs:w-[300px]"
          }`}
        >
          <span className="flex-1 text-left">
            {askPaperLabels["ask-this-paper-placeholder"]}
          </span>
          <i className="flex justify-center items-center p-5 w-5 h-5 text-xl icon-chevron-up" />
        </button>
      )}
    </div>
  );
}

export default AskPaperButton;
